import { Image, type ImageSourcePropType } from "react-native";
import type {
  EmbryoMonogenicFinding,
  EmbryoPolygenicRisk,
} from "@src/packages/cycles/hooks/useEmbryoReport";
import {
  getConditionIllustrationKey,
  type ConditionIllustrationKey,
} from "./conditionIllustrationKeys";
import adhd from "../../assets/images/genetic-conditions/adhd.png";
import ageRelatedMacularDegeneration from "../../assets/images/genetic-conditions/age-related-macular-degeneration.png";
import alcoholDependence from "../../assets/images/genetic-conditions/alcohol-dependence.png";
import alzheimerDisease from "../../assets/images/genetic-conditions/alzheimer-disease.png";
import anxietyDisorders from "../../assets/images/genetic-conditions/anxiety-disorders.png";
import asthma from "../../assets/images/genetic-conditions/asthma.png";
import atrialFibrillation from "../../assets/images/genetic-conditions/atrial-fibrillation.png";
import auditorySystem from "../../assets/images/genetic-conditions/auditory-system.png";
import autismSpectrumDisorder from "../../assets/images/genetic-conditions/autism-spectrum-disorder.png";
import bipolarDisorder from "../../assets/images/genetic-conditions/bipolar-disorder.png";
import breastCancer from "../../assets/images/genetic-conditions/breast-cancer.png";
import cardiovascularSystem from "../../assets/images/genetic-conditions/cardiovascular-system.png";
import celiacDisease from "../../assets/images/genetic-conditions/celiac-disease.png";
import colorectalCancer from "../../assets/images/genetic-conditions/colorectal-cancer.png";
import connectiveTissueSystem from "../../assets/images/genetic-conditions/connective-tissue-system.png";
import coronaryArteryDisease from "../../assets/images/genetic-conditions/coronary-artery-disease.png";
import cutaneousSystem from "../../assets/images/genetic-conditions/cutaneous-system.png";
import depression from "../../assets/images/genetic-conditions/depression.png";
import digestiveSystem from "../../assets/images/genetic-conditions/digestive-system.png";
import endocrineSystem from "../../assets/images/genetic-conditions/endocrine-system.png";
import endometriosis from "../../assets/images/genetic-conditions/endometriosis.png";
import geneticSystem from "../../assets/images/genetic-conditions/genetic-system.png";
import hematologicSystem from "../../assets/images/genetic-conditions/hematologic-system.png";
import hepaticSystem from "../../assets/images/genetic-conditions/hepatic-system.png";
import hereditaryCancerSystem from "../../assets/images/genetic-conditions/hereditary-cancer-system.png";
import hypertension from "../../assets/images/genetic-conditions/hypertension.png";
import immuneSystem from "../../assets/images/genetic-conditions/immune-system.png";
import inflammatoryBowelDisease from "../../assets/images/genetic-conditions/inflammatory-bowel-disease.png";
import insomnia from "../../assets/images/genetic-conditions/insomnia.png";
import maleBreastCancer from "../../assets/images/genetic-conditions/male-breast-cancer.png";
import malePatternBaldness from "../../assets/images/genetic-conditions/male-pattern-baldness.png";
import metabolicSystem from "../../assets/images/genetic-conditions/metabolic-system.png";
import migraine from "../../assets/images/genetic-conditions/migraine.png";
import multipleSclerosis from "../../assets/images/genetic-conditions/multiple-sclerosis.png";
import musculoskeletalSystem from "../../assets/images/genetic-conditions/musculoskeletal-system.png";
import neurologicSystem from "../../assets/images/genetic-conditions/neurologic-system.png";
import neuromuscularSystem from "../../assets/images/genetic-conditions/neuromuscular-system.png";
import obsessiveCompulsiveDisorder from "../../assets/images/genetic-conditions/obsessive-compulsive-disorder.png";
import osteoarthritis from "../../assets/images/genetic-conditions/osteoarthritis.png";
import ovarianCancer from "../../assets/images/genetic-conditions/ovarian-cancer.png";
import parkinsonDisease from "../../assets/images/genetic-conditions/parkinson-disease.png";
import polycysticOvarySyndrome from "../../assets/images/genetic-conditions/polycystic-ovary-syndrome.png";
import prostateCancer from "../../assets/images/genetic-conditions/prostate-cancer.png";
import psoriasis from "../../assets/images/genetic-conditions/psoriasis.png";
import renalSystem from "../../assets/images/genetic-conditions/renal-system.png";
import respiratorySystem from "../../assets/images/genetic-conditions/respiratory-system.png";
import restlessLegsSyndrome from "../../assets/images/genetic-conditions/restless-legs-syndrome.png";
import rheumatoidArthritis from "../../assets/images/genetic-conditions/rheumatoid-arthritis.png";
import schizophrenia from "../../assets/images/genetic-conditions/schizophrenia.png";
import seasonalAllergies from "../../assets/images/genetic-conditions/seasonal-allergies.png";
import severeAcne from "../../assets/images/genetic-conditions/severe-acne.png";
import type1Diabetes from "../../assets/images/genetic-conditions/type-1-diabetes.png";
import type2Diabetes from "../../assets/images/genetic-conditions/type-2-diabetes.png";
import visualSystem from "../../assets/images/genetic-conditions/visual-system.png";

const illustrations: Record<ConditionIllustrationKey, ImageSourcePropType> = {
  adhd: adhd,
  "age-related-macular-degeneration": ageRelatedMacularDegeneration,
  "alcohol-dependence": alcoholDependence,
  "alzheimer-disease": alzheimerDisease,
  "anxiety-disorders": anxietyDisorders,
  asthma: asthma,
  "atrial-fibrillation": atrialFibrillation,
  "auditory-system": auditorySystem,
  "autism-spectrum-disorder": autismSpectrumDisorder,
  "bipolar-disorder": bipolarDisorder,
  "breast-cancer": breastCancer,
  "cardiovascular-system": cardiovascularSystem,
  "celiac-disease": celiacDisease,
  "colorectal-cancer": colorectalCancer,
  "connective-tissue-system": connectiveTissueSystem,
  "coronary-artery-disease": coronaryArteryDisease,
  "cutaneous-system": cutaneousSystem,
  depression: depression,
  "digestive-system": digestiveSystem,
  "endocrine-system": endocrineSystem,
  endometriosis: endometriosis,
  "genetic-system": geneticSystem,
  "hematologic-system": hematologicSystem,
  "hepatic-system": hepaticSystem,
  "hereditary-cancer-system": hereditaryCancerSystem,
  hypertension: hypertension,
  "immune-system": immuneSystem,
  "inflammatory-bowel-disease": inflammatoryBowelDisease,
  insomnia: insomnia,
  "male-breast-cancer": maleBreastCancer,
  "male-pattern-baldness": malePatternBaldness,
  "metabolic-system": metabolicSystem,
  migraine: migraine,
  "multiple-sclerosis": multipleSclerosis,
  "musculoskeletal-system": musculoskeletalSystem,
  "neurologic-system": neurologicSystem,
  "neuromuscular-system": neuromuscularSystem,
  "obsessive-compulsive-disorder": obsessiveCompulsiveDisorder,
  osteoarthritis: osteoarthritis,
  "ovarian-cancer": ovarianCancer,
  "parkinson-disease": parkinsonDisease,
  "polycystic-ovary-syndrome": polycysticOvarySyndrome,
  "prostate-cancer": prostateCancer,
  psoriasis: psoriasis,
  "renal-system": renalSystem,
  "respiratory-system": respiratorySystem,
  "restless-legs-syndrome": restlessLegsSyndrome,
  "rheumatoid-arthritis": rheumatoidArthritis,
  schizophrenia: schizophrenia,
  "seasonal-allergies": seasonalAllergies,
  "severe-acne": severeAcne,
  "type-1-diabetes": type1Diabetes,
  "type-2-diabetes": type2Diabetes,
  "visual-system": visualSystem,
  // Shared neutral metabolism artwork; no body-size or weight imagery.
  "class-3-obesity": metabolicSystem,
};

type ConditionIllustrationProps = {
  condition:
    EmbryoMonogenicFinding["condition"] | EmbryoPolygenicRisk["condition"];
};

/** Decorative artwork in the canonical Row leading slot; the reported label is unchanged. */
export default function ConditionIllustration({
  condition,
}: ConditionIllustrationProps) {
  const key = getConditionIllustrationKey(condition) ?? "genetic-system";
  return (
    <Image
      source={illustrations[key]}
      style={{ width: 34, height: 34 }}
      resizeMode="contain"
      accessible={false}
      importantForAccessibility="no"
      testID={`condition-illustration-${key}`}
    />
  );
}
